// src/app/page.tsx (or your password generator page file)
"use client";

import React, { useState, useEffect, useReducer, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Slider } from "@/app/components/ui/slider";
import { Switch } from "@/app/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/app/components/ui/accordion";
import { Badge } from "@/app/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Textarea } from "@/app/components/ui/textarea";
import { 
  Eye, EyeOff, RefreshCw, Shield, Zap, Info, History, Trash2, Clock, Loader2, 
  Download, Upload, Settings, CheckCircle, XCircle, AlertTriangle, Lock, 
  Unlock, Key, FileText, Save, Loader, Sparkles, Target, Database, Globe, Heart
} from "lucide-react";
import { CopyButton } from "@/app/components/ui/copy-button";
import { useAuth } from "@/hooks/useAuth";
import { databaseService } from "@/lib/database-service";
import { trpc } from "@/lib/trpc";
import toast from "react-hot-toast";
import type { PasswordSettings } from "@/lib/password-service";
import { generateRandomPassword, generatePassphrase, calculateEntropy, getStrengthInfo, estimateCrackTime } from "@/lib/password-service";

// --- PASSWORD TEMPLATES ---
const PASSWORD_TEMPLATES = {
  "smart": {
    name: "Smart Password",
    description: "AI-optimized secure password",
    settings: {
      passwordType: "smart" as const,
      length: 16,
      includeUppercase: true,
      includeLowercase: true,
      includeNumbers: true,
      includeSymbols: true,
      excludeSimilar: true,
      excludeAmbiguous: true,
      minDigits: 2,
      minSymbols: 2,
      wordCount: 5,
      separator: "-",
      capitalizeWords: true,
      addNumbers: true,
      pinLength: 6,
    }
  },
  "random": {
    name: "Random Password",
    description: "Cryptographically secure random",
    settings: {
      passwordType: "random" as const,
      length: 16,
      includeUppercase: true,
      includeLowercase: true,
      includeNumbers: true,
      includeSymbols: true,
      excludeSimilar: false,
      excludeAmbiguous: false,
      minDigits: 2,
      minSymbols: 2,
      wordCount: 5,
      separator: "-",
      capitalizeWords: true,
      addNumbers: true,
      pinLength: 6,
    }
  },
  "memorable": {
    name: "Memorable Password",
    description: "Easy to remember passphrase",
    settings: {
      passwordType: "memorable" as const,
      length: 18,
      includeUppercase: false,
      includeLowercase: true,
      includeNumbers: false,
      includeSymbols: false,
      excludeSimilar: false,
      excludeAmbiguous: false,
      minDigits: 0,
      minSymbols: 0,
      wordCount: 4,
      separator: " ",
      capitalizeWords: true,
      addNumbers: true,
      pinLength: 6,
    }
  },
  "pin-code": {
    name: "PIN Code",
    description: "Numeric PIN for devices",
    settings: {
      passwordType: "pin" as const,
      length: 16,
      includeUppercase: false,
      includeLowercase: false,
      includeNumbers: true,
      includeSymbols: false,
      excludeSimilar: false,
      excludeAmbiguous: false,
      minDigits: 0,
      minSymbols: 0,
      wordCount: 5,
      separator: "-",
      capitalizeWords: true,
      addNumbers: true,
      pinLength: 6,
    }
  },
  "web-login": {
    name: "Web Login",
    description: "Standard web login password",
    settings: {
      passwordType: "random" as const,
      length: 16,
      includeUppercase: true,
      includeLowercase: true,
      includeNumbers: true,
      includeSymbols: true,
      excludeSimilar: false,
      excludeAmbiguous: false,
      minDigits: 2,
      minSymbols: 2,
      wordCount: 5,
      separator: "-",
      capitalizeWords: true,
      addNumbers: true,
      pinLength: 6,
    }
  },
  "banking": {
    name: "Banking",
    description: "High-security banking password",
    settings: {
      passwordType: "smart" as const,
      length: 20,
      includeUppercase: true,
      includeLowercase: true,
      includeNumbers: true,
      includeSymbols: true,
      excludeSimilar: true,
      excludeAmbiguous: true,
      minDigits: 3,
      minSymbols: 3,
      wordCount: 6,
      separator: "-",
      capitalizeWords: true,
      addNumbers: true,
      pinLength: 6,
    }
  },
  "api-key": {
    name: "API Key",
    description: "Long API key format",
    settings: {
      passwordType: "random" as const,
      length: 32,
      includeUppercase: true,
      includeLowercase: true,
      includeNumbers: true,
      includeSymbols: false,
      excludeSimilar: true,
      excludeAmbiguous: false,
      minDigits: 4,
      minSymbols: 0,
      wordCount: 5,
      separator: "-",
      capitalizeWords: true,
      addNumbers: true,
      pinLength: 6,
    }
  }
};

// --- PASSWORD VALIDATION ---
interface PasswordValidation {
  hasMinLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumbers: boolean;
  hasSymbols: boolean;
  hasNoRepeatingChars: boolean;
  hasNoSequentialChars: boolean;
  hasNoCommonPatterns: boolean;
  score: number;
  suggestions: string[];
}

const validatePassword = (password: string, settings: PasswordSettings): PasswordValidation => {
  const suggestions: string[] = [];
  let score = 0;

  // Basic checks
  const hasMinLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSymbols = /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password);
  
  // Advanced checks
  const hasNoRepeatingChars = !/(.)\1{2,}/.test(password);
  const hasNoSequentialChars = !/(?:abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|123|234|345|456|567|678|789|012)/i.test(password);
  const hasNoCommonPatterns = !/(?:password|123456|qwerty|admin|letmein|welcome|monkey|dragon|master|football)/i.test(password);

  // Score calculation
  if (hasMinLength) score += 20;
  if (hasUppercase) score += 15;
  if (hasLowercase) score += 15;
  if (hasNumbers) score += 15;
  if (hasSymbols) score += 15;
  if (hasNoRepeatingChars) score += 10;
  if (hasNoSequentialChars) score += 5;
  if (hasNoCommonPatterns) score += 5;

  // Suggestions
  if (!hasMinLength) suggestions.push("Use at least 8 characters");
  if (!hasUppercase) suggestions.push("Add uppercase letters");
  if (!hasLowercase) suggestions.push("Add lowercase letters");
  if (!hasNumbers) suggestions.push("Add numbers");
  if (!hasSymbols) suggestions.push("Add symbols");
  if (!hasNoRepeatingChars) suggestions.push("Avoid repeating characters");
  if (!hasNoSequentialChars) suggestions.push("Avoid sequential characters");
  if (!hasNoCommonPatterns) suggestions.push("Avoid common patterns");

  return {
    hasMinLength,
    hasUppercase,
    hasLowercase,
    hasNumbers,
    hasSymbols,
    hasNoRepeatingChars,
    hasNoSequentialChars,
    hasNoCommonPatterns,
    score,
    suggestions
  };
};

// --- STATE MANAGEMENT ---
const initialSettings: PasswordSettings = {
  passwordType: "smart",
  length: 18,
  includeUppercase: true,
  includeLowercase: true,
  includeNumbers: true,
  includeSymbols: true,
  excludeSimilar: false,
  excludeAmbiguous: false,
  minDigits: 1,
  minSymbols: 1,
  wordCount: 5,
  separator: "-",
  capitalizeWords: true,
  addNumbers: true,
  pinLength: 6,
};

function settingsReducer(state: typeof initialSettings, action: { type: string; payload: any }) {
  switch (action.type) {
    case 'SET_VALUE':
      return { ...state, [action.payload.key]: action.payload.value };
    case 'SET_TYPE':
        return {...state, passwordType: action.payload };
    case 'LOAD_TEMPLATE':
        return {...state, ...action.payload };
    case 'RESET':
        return initialSettings;
    default:
      return state;
  }
}

// --- NEW PASSWORD GENERATION FUNCTIONS ---

// Smart password generation with contextual rules
const generateSmartPassword = (settings: PasswordSettings): string => {
  const { length, includeUppercase, includeLowercase, includeNumbers, includeSymbols, excludeSimilar, excludeAmbiguous } = settings;
  
  let charset = '';
  if (includeLowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
  if (includeUppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  if (includeNumbers) charset += '0123456789';
  if (includeSymbols) charset += '!@#$%^&*()_+-=[]{}|;:,.<>?';
  
  // Remove similar characters if requested
  if (excludeSimilar) {
    charset = charset.replace(/[il1Lo0O]/g, '');
  }
  
  // Remove ambiguous characters if requested
  if (excludeAmbiguous) {
    charset = charset.replace(/[{}[\]()\/\\'";:<>.,]/g, '');
  }
  
  // Smart password rules
  let password = '';
  const hasLower = includeLowercase;
  const hasUpper = includeUppercase;
  const hasNum = includeNumbers;
  const hasSym = includeSymbols;
  
  // Ensure at least one character from each required type
  if (hasLower) password += charset.match(/[a-z]/)?.[0] || 'a';
  if (hasUpper) password += charset.match(/[A-Z]/)?.[0] || 'A';
  if (hasNum) password += charset.match(/[0-9]/)?.[0] || '1';
  if (hasSym) password += charset.match(/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/)?.[0] || '!';
  
  // Fill the rest randomly
  for (let i = password.length; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    password += charset[randomIndex];
  }
  
  // Shuffle the password to avoid predictable patterns
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

// Enhanced memorable password with better word selection
const generateMemorablePassword = (settings: PasswordSettings, wordlist: string[]): string => {
  const { wordCount, separator, capitalizeWords, addNumbers } = settings;
  
  // Filter for common, memorable words (4-8 characters)
  const memorableWords = wordlist.filter(word => 
    word.length >= 4 && 
    word.length <= 8 && 
    /^[a-z]+$/.test(word) &&
    !word.includes('ing') && // Avoid common endings
    !word.includes('tion') &&
    !word.includes('ness')
  );
  
  if (memorableWords.length === 0) {
    // Fallback to basic wordlist
    return generatePassphrase(settings, wordlist);
  }
  
  const words: string[] = [];
  for (let i = 0; i < wordCount; i++) {
    const randomIndex = Math.floor(Math.random() * memorableWords.length);
    let word = memorableWords[randomIndex];
    
    if (capitalizeWords) {
      word = word.charAt(0).toUpperCase() + word.slice(1);
    }
    
    words.push(word);
  }
  
  let password = words.join(separator);
  
  if (addNumbers) {
    // Add 1-2 random numbers at the end
    const numCount = Math.floor(Math.random() * 2) + 1;
    for (let i = 0; i < numCount; i++) {
      password += Math.floor(Math.random() * 10);
    }
  }
  
  return password;
};

// PIN code generation
const generatePinCode = (settings: PasswordSettings): string => {
  const { pinLength } = settings;
  let pin = '';
  
  // Avoid starting with 0 and avoid common patterns
  const firstDigit = Math.floor(Math.random() * 9) + 1;
  pin += firstDigit;
  
  for (let i = 1; i < pinLength; i++) {
    let digit;
    do {
      digit = Math.floor(Math.random() * 10);
    } while (
      // Avoid repeating the same digit 3 times in a row
      (i >= 2 && digit === parseInt(pin[i-1]) && digit === parseInt(pin[i-2])) ||
      // Avoid sequential patterns
      (i >= 2 && (
        (digit === parseInt(pin[i-1]) + 1 && parseInt(pin[i-1]) === parseInt(pin[i-2]) + 1) ||
        (digit === parseInt(pin[i-1]) - 1 && parseInt(pin[i-1]) === parseInt(pin[i-2]) - 1)
      ))
    );
    pin += digit;
  }
  
  return pin;
};

// --- MAIN COMPONENT ---
export default function PasswordGeneratorPage() {
  const { user } = useAuth();
  const [settings, dispatch] = useReducer(settingsReducer, initialSettings);
  const [password, setPassword] = useState("");
  const [wordlist, setWordlist] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [entropy, setEntropy] = useState(0);
  const [crackTime, setCrackTime] = useState("");
  const [history, setHistory] = useState<string[]>([]);
  const [copyAnimation, setCopyAnimation] = useState(false);
  const [historyCopyIndex, setHistoryCopyIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingHistory, setSavingHistory] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [validation, setValidation] = useState<PasswordValidation | null>(null);
  const [breachCheckLoading, setBreachCheckLoading] = useState(false);
  const [breachResult, setBreachResult] = useState<{breached: boolean, count?: number} | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  // --- TRPC HOOKS ---
  const wordsQuery = trpc.words.list.useQuery();
  const activityLogMutation = trpc.activity.log.useMutation();

  // --- DATA & STATE HOOKS ---

  // Load wordlist on mount
  useEffect(() => {
    if (wordsQuery.data?.words) {
      setWordlist(wordsQuery.data.words);
    } else if (wordsQuery.error) {
      toast.error("Could not load passphrase wordlist.");
    }
  }, [wordsQuery.data, wordsQuery.error]);

  // Load user preferences and history on mount
  useEffect(() => {
    if (user) {
      loadUserData();
    } else {
      // Load history from localStorage for non-authenticated users
      loadLocalHistory();
    }
  }, [user]);

  // Validate password when it changes
  useEffect(() => {
    if (password) {
      const validationResult = validatePassword(password, settings);
      setValidation(validationResult);
    } else {
      setValidation(null);
    }
  }, [password, settings]);

  const loadUserData = async () => {
    if (!user) return;

    try {
      // Optional: load preferences from your own API later
    } catch (error) {
      console.error('Error loading user data:', error);
      // Fallback to localStorage
      loadLocalHistory();
    }
  };

  const loadLocalHistory = () => {
    try {
      const storedHistory = localStorage.getItem("passwordHistory");
      if (storedHistory) {
        setHistory(JSON.parse(storedHistory));
      }
    } catch (error) {
      console.error("Failed to parse history from localStorage", error);
    }
  };

  // Save user preferences
  const saveUserPreferences = async () => {
    if (!user) return;

    try {
      await databaseService.saveUserPreferences(user.$id, 'password-generator', settings);
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  // Save password to history
  const savePasswordToHistory = async (newPassword: string) => {
    if (!user) {
      // For non-authenticated users, save to localStorage
      if (newPassword && !history.includes(newPassword)) {
        const updatedHistory = [newPassword, ...history].slice(0, 10);
        setHistory(updatedHistory);
        localStorage.setItem("passwordHistory", JSON.stringify(updatedHistory));
      }
      return;
    }

    // For authenticated users, log server-side activity only
    setSavingHistory(true);
    try {
      await activityLogMutation.mutateAsync({
        activityType: 'password_generated',
        toolName: 'password-generator',
        metadata: {
          password_type: settings.passwordType,
          length: settings.passwordType === 'random' ? settings.length : settings.wordCount,
        },
      });

      // Update local history with placeholder
      const updatedHistory = ['••••••••', ...history].slice(0, 10);
      setHistory(updatedHistory);
    } catch (error) {
      console.error('Error logging password activity:', error);
    } finally {
      setSavingHistory(false);
    }
  };

  // Generate password logic
  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    
    // Add a brief delay to show the generating effect
    await new Promise(resolve => setTimeout(resolve, 300));
    
    let newPassword = "";
    if (settings.passwordType === "smart") {
      newPassword = generateSmartPassword(settings);
    } else if (settings.passwordType === "random") {
      newPassword = generateRandomPassword(settings);
    } else if (settings.passwordType === "memorable") {
      newPassword = generateMemorablePassword(settings, wordlist);
    } else if (settings.passwordType === "pin") {
      newPassword = generatePinCode(settings);
    } else if (settings.passwordType === "passphrase") {
      newPassword = generatePassphrase(settings, wordlist);
    }
    setPassword(newPassword);
    
    // Save to history
    savePasswordToHistory(newPassword);
    
    setIsGenerating(false);
    
    // Save preferences for authenticated users
    // Optionally save preferences via API later if needed
  }, [settings, wordlist, user]);

  // Auto-generate on mount and settings change
  useEffect(() => {
    if (wordlist.length > 0) {
      handleGenerate();
    }
  }, [wordlist, handleGenerate]);

  // --- NEW FEATURES ---

  // Load template
  const loadTemplate = (templateKey: string) => {
    const template = PASSWORD_TEMPLATES[templateKey as keyof typeof PASSWORD_TEMPLATES];
    if (template) {
      dispatch({ type: 'LOAD_TEMPLATE', payload: template.settings });
      setSelectedTemplate(templateKey);
      toast.success(`Loaded ${template.name} template`);
    }
  };

  // Export settings
  const exportSettings = () => {
    const data = {
      settings,
      timestamp: new Date().toISOString(),
      version: "1.0"
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'password-settings.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Settings exported successfully');
  };

  // Import settings
  const importSettings = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        if (data.settings) {
          dispatch({ type: 'LOAD_TEMPLATE', payload: data.settings });
          toast.success('Settings imported successfully');
        }
      } catch (error) {
        toast.error('Invalid settings file');
      }
    };
    reader.readAsText(file);
  };

  // Check password breach
  const checkPasswordBreach = async () => {
    if (!password) return;
    
    setBreachCheckLoading(true);
    setBreachResult(null);
    
    try {
      // Simulate API call to breach checking service
      // In real implementation, you'd call a service like HaveIBeenPwned
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Simulate result (in real app, this would be from API)
      const isBreached = Math.random() > 0.8; // 20% chance for demo
      const breachCount = isBreached ? Math.floor(Math.random() * 1000000) : 0;
      
      setBreachResult({
        breached: isBreached,
        count: breachCount
      });
      
      if (isBreached) {
        toast.error(`Password found in ${breachCount.toLocaleString()} data breaches!`);
      } else {
        toast.success('Password not found in known breaches');
      }
    } catch (error) {
      toast.error('Failed to check password breach');
    } finally {
      setBreachCheckLoading(false);
    }
  };

  // Generate multiple passwords
  const generateMultiplePasswords = () => {
    const passwords = [];
    for (let i = 0; i < 5; i++) {
      if (settings.passwordType === "smart") {
        passwords.push(generateSmartPassword(settings));
      } else if (settings.passwordType === "random") {
        passwords.push(generateRandomPassword(settings));
      } else if (settings.passwordType === "memorable") {
        passwords.push(generateMemorablePassword(settings, wordlist));
      } else if (settings.passwordType === "pin") {
        passwords.push(generatePinCode(settings));
      } else if (settings.passwordType === "passphrase") {
        passwords.push(generatePassphrase(settings, wordlist));
      }
    }
    return passwords;
  };

  // Save password to favorites
  const saveToFavorites = () => {
    if (!password) return;
    
    try {
      const favorites = JSON.parse(localStorage.getItem("passwordFavorites") || "[]");
      if (!favorites.includes(password)) {
        favorites.push(password);
        localStorage.setItem("passwordFavorites", JSON.stringify(favorites));
        toast.success("Password saved to favorites");
      } else {
        toast("Password already in favorites");
      }
    } catch (error) {
      toast.error("Failed to save to favorites");
    }
  };

  // --- COPY FUNCTIONALITY ---
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopyAnimation(true);
      toast.success("Password copied to clipboard!");
      setTimeout(() => setCopyAnimation(false), 200);
    }).catch(() => {
      toast.error("Failed to copy password");
    });
  };

  const handleHistoryCopy = (index: number) => {
    setHistoryCopyIndex(index);
    setTimeout(() => setHistoryCopyIndex(null), 200);
  };

  // --- HISTORY MANAGEMENT ---
  const clearHistory = async () => {
    if (user) {
      try {
        await databaseService.clearPasswordHistory(user.$id);
        setHistory([]);
        toast.success("Password history cleared");
        
        // Log activity
        await activityLogMutation.mutateAsync({
          activityType: 'password_history_cleared',
          toolName: 'password-generator'
        });
      } catch (error) {
        console.error('Error clearing password history:', error);
        toast.error('Failed to clear password history');
      }
    } else {
      setHistory([]);
      localStorage.removeItem("passwordHistory");
      toast.success("Password history cleared");
    }
  };

  // --- STRENGTH CALCULATION ---
  useEffect(() => {
    if (password && wordlist.length > 0) {
      const passwordEntropy = calculateEntropy(settings, wordlist.length);
      setEntropy(passwordEntropy);
      setCrackTime(estimateCrackTime(passwordEntropy));
    } else if (password && settings.passwordType === 'random') {
      // For random passwords, we can calculate entropy without wordlist
      const passwordEntropy = calculateEntropy(settings, 0);
      setEntropy(passwordEntropy);
      setCrackTime(estimateCrackTime(passwordEntropy));
    }
  }, [password, settings, wordlist.length]);

  const strengthInfo = getStrengthInfo(entropy);

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 animate-slide-in-left">Password Generator</h1>
          <p className="text-muted-foreground text-lg animate-slide-in-right">
            Generate secure passwords and passphrases with customizable settings
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Generator */}
          <div className="lg:col-span-2 space-y-6">
            {/* Generated Password */}
            <Card className="card-enhanced">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Generated Password
                </CardTitle>
                <CardDescription>
                  Your secure password is ready
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="relative focus-ring">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    readOnly
                    // increase right padding on small screens so the icons don't overlap / overflow
                    className="text-lg pr-28 md:pr-20 input-enhanced [font-family:var(--font-jetbrains-mono)]"
                    placeholder="Click generate to create a password"
                  />
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1 z-10">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      className="h-8 w-8 p-0"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <CopyButton
                      text={password}
                      variant="ghost"
                      size="sm"
                      className={`h-8 w-8 p-0 ${copyAnimation ? 'scale-110' : ''}`}
                      showTextOnCopy={false}
                      onCopy={() => {
                        setCopyAnimation(true);
                        setTimeout(() => setCopyAnimation(false), 200);
                      }}
                    />
                  </div>
                </div>

                {/* Password Strength */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Password Strength</span>
                    <span className={`text-sm font-medium ${strengthInfo.color}`}>
                      {strengthInfo.label}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${strengthInfo.color}`}
                      style={{ width: `${Math.min(100, (entropy / 128) * 100)}%` }}
                    ></div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Entropy: {entropy.toFixed(1)} bits</span>
                    <span>Crack time: {crackTime}</span>
                  </div>
                </div>

                {/* Password Validation */}
                {validation && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Validation Score: {validation.score}/100</span>
                      {validation.score >= 80 ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-yellow-500" />
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {Object.entries({
                        'Min Length': validation.hasMinLength,
                        'Uppercase': validation.hasUppercase,
                        'Lowercase': validation.hasLowercase,
                        'Numbers': validation.hasNumbers,
                        'Symbols': validation.hasSymbols,
                        'No Repeats': validation.hasNoRepeatingChars,
                        'No Sequential': validation.hasNoSequentialChars,
                        'No Patterns': validation.hasNoCommonPatterns,
                      }).map(([label, passed]) => (
                        <div key={label} className="flex items-center gap-1">
                          {passed ? (
                            <CheckCircle className="h-3 w-3 text-green-500" />
                          ) : (
                            <XCircle className="h-3 w-3 text-red-500" />
                          )}
                          <span className={passed ? 'text-green-700' : 'text-red-700'}>{label}</span>
                        </div>
                      ))}
                    </div>
                    {validation.suggestions.length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        <strong>Suggestions:</strong> {validation.suggestions.slice(0, 3).join(', ')}
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={handleGenerate}
                    disabled={loading || savingHistory || isGenerating}
                    className={`btn-enhanced hover-lift ${isGenerating ? 'animate-pulse' : ''}`}
                  >
                    {(loading || savingHistory || isGenerating) ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    {isGenerating ? "Generating..." : "Generate New Password"}
                  </Button>

                  <Button
                    onClick={checkPasswordBreach}
                    disabled={!password || breachCheckLoading}
                    variant="outline"
                    className="btn-enhanced hover-lift"
                  >
                    {breachCheckLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Database className="h-4 w-4 mr-2" />
                    )}
                    Check Breach
                  </Button>

                  <Button
                    onClick={saveToFavorites}
                    disabled={!password}
                    variant="outline"
                    className="btn-enhanced hover-lift"
                  >
                    <Heart className="h-4 w-4 mr-2" />
                    Save to Favorites
                  </Button>
                </div>

                {/* Breach Check Result */}
                {breachResult && (
                  <div className={`p-3 rounded-lg border ${
                    breachResult.breached 
                      ? 'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800' 
                      : 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800'
                  }`}>
                    <div className="flex items-center gap-2">
                      {breachResult.breached ? (
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                      ) : (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      )}
                      <span className={`text-sm font-medium ${
                        breachResult.breached ? 'text-red-800 dark:text-red-200' : 'text-green-800 dark:text-green-200'
                      }`}>
                        {breachResult.breached 
                          ? `Password found in ${breachResult.count?.toLocaleString()} data breaches!` 
                          : 'Password not found in known breaches'
                        }
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Settings */}
            <Card className="card-enhanced">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    <CardTitle>Password Settings</CardTitle>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      {showAdvanced ? 'Basic' : 'Advanced'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={exportSettings}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".json"
                        onChange={importSettings}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="pointer-events-none"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Import
                      </Button>
                    </div>
                  </div>
                </div>
                <CardDescription>
                  Customize your password generation preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <Tabs value={settings.passwordType} onValueChange={(value) => dispatch({ type: 'SET_TYPE', payload: value })}>
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="smart">Smart</TabsTrigger>
                    <TabsTrigger value="random">Random</TabsTrigger>
                    <TabsTrigger value="memorable">Memorable</TabsTrigger>
                    <TabsTrigger value="pin">PIN Code</TabsTrigger>
                  </TabsList>

                  <TabsContent value="smart" className="space-y-4">
                    <div className="space-y-4">
                      <div>
                        <Label>Password Length: {settings.length}</Label>
                        <Slider
                          value={[settings.length]}
                          onValueChange={(value) => dispatch({ type: 'SET_VALUE', payload: { key: 'length', value: value[0] } })}
                          max={128}
                          min={8}
                          step={1}
                          className="mt-2"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="uppercase"
                            checked={settings.includeUppercase}
                            onCheckedChange={(checked) => dispatch({ type: 'SET_VALUE', payload: { key: 'includeUppercase', value: checked } })}
                          />
                          <Label htmlFor="uppercase">Uppercase (A-Z)</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="lowercase"
                            checked={settings.includeLowercase}
                            onCheckedChange={(checked) => dispatch({ type: 'SET_VALUE', payload: { key: 'includeLowercase', value: checked } })}
                          />
                          <Label htmlFor="lowercase">Lowercase (a-z)</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="numbers"
                            checked={settings.includeNumbers}
                            onCheckedChange={(checked) => dispatch({ type: 'SET_VALUE', payload: { key: 'includeNumbers', value: checked } })}
                          />
                          <Label htmlFor="numbers">Numbers (0-9)</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="symbols"
                            checked={settings.includeSymbols}
                            onCheckedChange={(checked) => dispatch({ type: 'SET_VALUE', payload: { key: 'includeSymbols', value: checked } })}
                          />
                          <Label htmlFor="symbols">Symbols (!@#$%^&*)</Label>
                        </div>
                      </div>

                      {showAdvanced && (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center space-x-2">
                              <Switch
                                id="excludeSimilar"
                                checked={settings.excludeSimilar}
                                onCheckedChange={(checked) => dispatch({ type: 'SET_VALUE', payload: { key: 'excludeSimilar', value: checked } })}
                              />
                              <Label htmlFor="excludeSimilar">Exclude similar characters</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Switch
                                id="excludeAmbiguous"
                                checked={settings.excludeAmbiguous}
                                onCheckedChange={(checked) => dispatch({ type: 'SET_VALUE', payload: { key: 'excludeAmbiguous', value: checked } })}
                              />
                              <Label htmlFor="excludeAmbiguous">Exclude ambiguous characters</Label>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Minimum Digits: {settings.minDigits}</Label>
                              <Slider
                                value={[settings.minDigits]}
                                onValueChange={(value) => dispatch({ type: 'SET_VALUE', payload: { key: 'minDigits', value: value[0] } })}
                                max={10}
                                min={0}
                                step={1}
                                className="mt-2"
                              />
                            </div>
                            <div>
                              <Label>Minimum Symbols: {settings.minSymbols}</Label>
                              <Slider
                                value={[settings.minSymbols]}
                                onValueChange={(value) => dispatch({ type: 'SET_VALUE', payload: { key: 'minSymbols', value: value[0] } })}
                                max={10}
                                min={0}
                                step={1}
                                className="mt-2"
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="random" className="space-y-4">
                    <div className="space-y-4">
                      <div>
                        <Label>Password Length: {settings.length}</Label>
                        <Slider
                          value={[settings.length]}
                          onValueChange={(value) => dispatch({ type: 'SET_VALUE', payload: { key: 'length', value: value[0] } })}
                          max={128}
                          min={8}
                          step={1}
                          className="mt-2"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="uppercase"
                            checked={settings.includeUppercase}
                            onCheckedChange={(checked) => dispatch({ type: 'SET_VALUE', payload: { key: 'includeUppercase', value: checked } })}
                          />
                          <Label htmlFor="uppercase">Uppercase (A-Z)</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="lowercase"
                            checked={settings.includeLowercase}
                            onCheckedChange={(checked) => dispatch({ type: 'SET_VALUE', payload: { key: 'includeLowercase', value: checked } })}
                          />
                          <Label htmlFor="lowercase">Lowercase (a-z)</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="numbers"
                            checked={settings.includeNumbers}
                            onCheckedChange={(checked) => dispatch({ type: 'SET_VALUE', payload: { key: 'includeNumbers', value: checked } })}
                          />
                          <Label htmlFor="numbers">Numbers (0-9)</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="symbols"
                            checked={settings.includeSymbols}
                            onCheckedChange={(checked) => dispatch({ type: 'SET_VALUE', payload: { key: 'includeSymbols', value: checked } })}
                          />
                          <Label htmlFor="symbols">Symbols (!@#$%^&*)</Label>
                        </div>
                      </div>

                      {showAdvanced && (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center space-x-2">
                              <Switch
                                id="excludeSimilar"
                                checked={settings.excludeSimilar}
                                onCheckedChange={(checked) => dispatch({ type: 'SET_VALUE', payload: { key: 'excludeSimilar', value: checked } })}
                              />
                              <Label htmlFor="excludeSimilar">Exclude similar characters</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Switch
                                id="excludeAmbiguous"
                                checked={settings.excludeAmbiguous}
                                onCheckedChange={(checked) => dispatch({ type: 'SET_VALUE', payload: { key: 'excludeAmbiguous', value: checked } })}
                              />
                              <Label htmlFor="excludeAmbiguous">Exclude ambiguous characters</Label>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Minimum Digits: {settings.minDigits}</Label>
                              <Slider
                                value={[settings.minDigits]}
                                onValueChange={(value) => dispatch({ type: 'SET_VALUE', payload: { key: 'minDigits', value: value[0] } })}
                                max={10}
                                min={0}
                                step={1}
                                className="mt-2"
                              />
                            </div>
                            <div>
                              <Label>Minimum Symbols: {settings.minSymbols}</Label>
                              <Slider
                                value={[settings.minSymbols]}
                                onValueChange={(value) => dispatch({ type: 'SET_VALUE', payload: { key: 'minSymbols', value: value[0] } })}
                                max={10}
                                min={0}
                                step={1}
                                className="mt-2"
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="memorable" className="space-y-4">
                    <div className="space-y-4">
                      <div>
                        <Label>Number of Words: {settings.wordCount}</Label>
                        <Slider
                          value={[settings.wordCount]}
                          onValueChange={(value) => dispatch({ type: 'SET_VALUE', payload: { key: 'wordCount', value: value[0] } })}
                          max={20}
                          min={3}
                          step={1}
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label>Word Separator</Label>
                        <Input
                          value={settings.separator}
                          onChange={(e) => dispatch({ type: 'SET_VALUE', payload: { key: 'separator', value: e.target.value } })}
                          placeholder="Separator between words"
                          className="mt-2"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="capitalizeWords"
                            checked={settings.capitalizeWords}
                            onCheckedChange={(checked) => dispatch({ type: 'SET_VALUE', payload: { key: 'capitalizeWords', value: checked } })}
                          />
                          <Label htmlFor="capitalizeWords">Capitalize words</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="addNumbers"
                            checked={settings.addNumbers}
                            onCheckedChange={(checked) => dispatch({ type: 'SET_VALUE', payload: { key: 'addNumbers', value: checked } })}
                          />
                          <Label htmlFor="addNumbers">Add numbers</Label>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="pin" className="space-y-4">
                    <div className="space-y-4">
                      <div>
                        <Label>PIN Length: {settings.pinLength}</Label>
                        <Slider
                          value={[settings.pinLength]}
                          onValueChange={(value) => dispatch({ type: 'SET_VALUE', payload: { key: 'pinLength', value: value[0] } })}
                          max={12}
                          min={4}
                          step={1}
                          className="mt-2"
                        />
                      </div>

                      <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="flex items-start gap-2">
                          <Info className="h-4 w-4 text-blue-600 mt-0.5" />
                          <div className="text-sm text-blue-800 dark:text-blue-200">
                            <p className="font-medium mb-1">Smart PIN Features:</p>
                            <ul className="space-y-1 text-xs">
                              <li>• Avoids common patterns (1234, 0000)</li>
                              <li>• Prevents sequential numbers</li>
                              <li>• No repeating digits (1111)</li>
                              <li>• Starts with non-zero digit</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* Password Templates */}
            <Card className="card-enhanced">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Password Templates
                </CardTitle>
                <CardDescription>
                  Quick-start with predefined password types
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(PASSWORD_TEMPLATES).map(([key, template]) => {
                    const active = selectedTemplate === key;
                    return (
                      <Button
                        key={key}
                        variant={active ? "default" : "outline"}
                        onClick={() => loadTemplate(key)}
                        aria-current={active ? 'true' : undefined}
                        className={`h-auto p-3 flex flex-col items-start gap-1 ${active ? 'btn-enhanced gradient-primary text-white' : ''}`}
                      >
                        <span className="font-medium">{template.name}</span>
                        <span className={`text-xs ${active ? 'text-white/90 font-semibold' : 'text-muted-foreground'}`}>{template.description}</span>
                      </Button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Password History */}
            <Card className="card-enhanced">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Password History
                </CardTitle>
                <CardDescription>
                  Recently generated passwords
                </CardDescription>
              </CardHeader>
              <CardContent>
                {history.length > 0 ? (
                  <div className="space-y-2">
                    {history.slice(0, 5).map((pwd, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted rounded text-sm">
                        <span className="truncate flex-1 mr-2 [font-family:var(--font-jetbrains-mono)]" title={pwd}>
                          {pwd}
                        </span>
                        <CopyButton
                          text={pwd === '••••••••' ? password : pwd}
                          variant="ghost"
                          size="sm"
                          showTextOnCopy={false}
                          onCopy={() => handleHistoryCopy(index)}
                          className={`h-6 w-6 p-0 shrink-0 ${historyCopyIndex === index ? 'scale-110' : ''}`}
                        />
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearHistory}
                      className="w-full mt-2"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Clear History
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    <History className="h-8 w-8 mx-auto mb-2" />
                    <p>No password history</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Password Tips */}
            <Card className="card-enhanced">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  Password Tips
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm space-y-2">
                  <p>• Use at least 12 characters for better security</p>
                  <p>• Include uppercase, lowercase, numbers, and symbols</p>
                  <p>• Avoid common patterns and personal information</p>
                  <p>• Consider using passphrases for easier memorization</p>
                  <p>• Use a password manager for secure storage</p>
                  <p>• Check if your password has been compromised</p>
                  <p>• Use different passwords for different accounts</p>
                </div>
              </CardContent>
            </Card>

            {/* Security Score */}
            <Card className="card-enhanced">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Security Score
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {validation ? validation.score : 0}/100
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {validation && validation.score >= 80 ? 'Excellent' : 
                     validation && validation.score >= 60 ? 'Good' : 
                     validation && validation.score >= 40 ? 'Fair' : 'Poor'}
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      validation && validation.score >= 80 ? 'bg-green-500' :
                      validation && validation.score >= 60 ? 'bg-blue-500' :
                      validation && validation.score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${validation ? validation.score : 0}%` }}
                  ></div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}